import type { PropsWithChildren } from 'react';
import { AlertCircle, CheckCircle2, Info, AlertTriangle } from 'lucide-react';
import { cn } from '@lib/utils/cn';

type Tone = 'info' | 'success' | 'warning' | 'danger';

const tones: Record<Tone, { wrap: string; icon: React.ReactNode }> = {
  info: { wrap: 'bg-info/10 text-info border-info/30', icon: <Info className="h-4 w-4" /> },
  success: {
    wrap: 'bg-success/10 text-success border-success/30',
    icon: <CheckCircle2 className="h-4 w-4" />,
  },
  warning: {
    wrap: 'bg-warning/10 text-warning border-warning/30',
    icon: <AlertTriangle className="h-4 w-4" />,
  },
  danger: {
    wrap: 'bg-danger/10 text-danger border-danger/30',
    icon: <AlertCircle className="h-4 w-4" />,
  },
};

export function Alert({
  tone = 'info',
  title,
  children,
  className,
}: PropsWithChildren<{ tone?: Tone; title?: string; className?: string }>) {
  const t = tones[tone];
  return (
    <div
      role="alert"
      className={cn('flex items-start gap-3 rounded-md border px-4 py-3 text-sm', t.wrap, className)}
    >
      <span className="shrink-0 mt-0.5">{t.icon}</span>
      <div className="flex-1">
        {title && <div className="font-semibold mb-1">{title}</div>}
        {children && <div className="text-fg">{children}</div>}
      </div>
    </div>
  );
}
